import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useQuery } from '@tanstack/react-query';
import Loader from '@components/Loader';
import classNames from 'classnames';
import { usePlayer } from '@context/PlayerContext';

import {
  buildCaptions,
  buildTranscript,
  parseSegments,
  findSearchMatches,
  searchHighlight,
  MatchType,
  Caption,
  SpeakerBlock
} from './utils';

import HeadPanel from './components/HeadPanel';
import SyncToPlaybackButton from './components/SyncToPlaybackButton';

import './TranscriptionContent.less';

export type TextModeType = 'captions' | 'transcript';

const EMPTY_RANGES: [number, number][] = [];
const EMPTY_MATCH_POSITIONS: { blockIndex: number; tokenIndex: number }[] = [];

type TranscriptionContentProps = {
  availableTranscripts: Record<string, string>;
  isAutoScrolling: boolean;
  setIsAutoScrolling: (value: boolean) => void;
};

type TranscriptTokenProps = {
  text: string;
  needsSpace: boolean;
  tokenRead: boolean;
  isActive: boolean;
  isMatched: boolean;
  ranges: [number, number][];
  searchMatchIndex: number;
  onActiveTokenRef: (el: HTMLSpanElement | null) => void;
};

type TranscriptionBlockProps = {
  item: Caption | SpeakerBlock;
  currentTimeMs: number | null;
  isCurrentTimeValid: boolean;
  searchMatches?: MatchType[];
  searchMatchIndex?: Record<number, number>;
  onActiveTokenRef: (el: HTMLSpanElement | null) => void;
};

const TranscriptToken = React.memo(function TranscriptToken({
  text,
  needsSpace,
  tokenRead,
  isActive,
  isMatched,
  ranges,
  searchMatchIndex,
  onActiveTokenRef
}: TranscriptTokenProps) {
  return (
    <>
      {needsSpace ? ' ' : ''}
      <span
        className={classNames({
          'b-token--read': tokenRead,
          'b-token--unread': !tokenRead,
          'b-token--active': isActive,
          'b-token--matched': isMatched
        })}
        data-search-match-index={
          searchMatchIndex !== -1 ? searchMatchIndex : undefined
        }
        ref={isActive ? onActiveTokenRef : null}
      >
        {ranges.length ? searchHighlight(text, ranges) : text}
      </span>
    </>
  );
});

const TranscriptionBlock = React.memo(function TranscriptionBlock({
  item,
  currentTimeMs,
  isCurrentTimeValid,
  searchMatches,
  searchMatchIndex,
  onActiveTokenRef
}: TranscriptionBlockProps) {
  const time =
    item.start !== item.end ? `${item.start} - ${item.end}` : item.start;

  const isBlockActive = useMemo((): boolean => {
    if (!isCurrentTimeValid || currentTimeMs === null) return false;

    return item.tokens.some(
      (t) =>
        t.start !== null &&
        t.end !== null &&
        t.start <= currentTimeMs &&
        t.end >= currentTimeMs
    );
  }, [item.tokens, currentTimeMs, isCurrentTimeValid]);

  let prevTokenRead = false;

  return (
    <div className="transcription-block">
      {'id' in item && item.id !== undefined && (
        <div className="transcription-block-number">{item.id}</div>
      )}

      <div className="transcription-block-body">
        <div className="transcription-block-head">
          {'speaker' in item && item.speaker !== undefined && (
            <div className="transcription-block-speaker">
              Speaker {item.speaker}
            </div>
          )}
          <div className="transcription-block-time">{time}</div>
        </div>

        <div className="transcription-block-text">
          {item.tokens.map((token, i) => {
            const text = token.text ?? '';
            const isPunctuation = text.length === 1 && '.,!?'.includes(text);
            const needsSpace = i > 0 && !isPunctuation;

            const tokenRead =
              token.start === null
                ? prevTokenRead
                : isCurrentTimeValid &&
                  currentTimeMs !== null &&
                  token.start <= currentTimeMs;

            const isActive =
              isBlockActive &&
              isCurrentTimeValid &&
              currentTimeMs !== null &&
              token.start !== null &&
              token.end !== null &&
              token.start <= currentTimeMs &&
              token.end >= currentTimeMs;

            prevTokenRead = tokenRead;

            const searchMatch = searchMatches?.[i];

            return (
              <TranscriptToken
                key={i}
                text={text}
                needsSpace={needsSpace}
                tokenRead={tokenRead}
                isActive={isActive}
                isMatched={!!searchMatch?.matched}
                ranges={searchMatch?.ranges || EMPTY_RANGES}
                searchMatchIndex={searchMatchIndex?.[i] ?? -1}
                onActiveTokenRef={onActiveTokenRef}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
});

export default function TranscriptionContent({
  availableTranscripts,
  isAutoScrolling,
  setIsAutoScrolling
}: TranscriptionContentProps) {
  const { player } = usePlayer();
  const [textMode, setTextMode] = useState<TextModeType>('transcript');
  const [searchValue, setSearchValue] = useState('');
  const [currentTimeMs, setCurrentTimeMs] = useState<number | null>(null);
  const [activeSearchMatchIndex, setActiveSearchMatchIndex] = useState(-1);

  const activeTokenElementRef = useRef<HTMLSpanElement | null>(null);
  const lastScrollTsRef = useRef<number>(0);
  const rafIdRef = useRef<number | null>(null);

  const shouldScrollToSearchMatchRef = useRef(false);
  const contentBodyRef = useRef<HTMLDivElement | null>(null);

  const searchText = searchValue?.trim().toLowerCase();
  const fileUrl = availableTranscripts.url;

  const transcriptCacheKey = useMemo(() => {
    if (!fileUrl) return null;
    try {
      return new URL(fileUrl).pathname;
    } catch {
      return fileUrl;
    }
  }, [fileUrl]);
  const isCurrentTimeValid =
    currentTimeMs !== null && !Number.isNaN(currentTimeMs);

  const { data, isLoading } = useQuery({
    queryKey: ['transcript', transcriptCacheKey],
    enabled: !!fileUrl,
    queryFn: async () => {
      const res = await fetch(fileUrl);
      if (!res.ok) throw new Error('Failed to fetch');
      return parseSegments(await res.text());
    }
  });

  const content = useMemo(() => {
    if (!data) return null;
    return textMode === 'captions'
      ? buildCaptions(data)
      : buildTranscript(data);
  }, [data, textMode]);

  const searchMatches = useMemo(() => {
    if (!content) return null;
    if (!searchText) return null;
    return content.map((item) => findSearchMatches(item.tokens, searchText));
  }, [content, searchText]);

  const searchMatchPositions = useMemo(() => {
    if (!content || !searchMatches) return EMPTY_MATCH_POSITIONS;
    if (!searchText.trim()) return EMPTY_MATCH_POSITIONS;

    const parts = searchText.split(/\s+/).filter(Boolean);
    const isPhraseSearch = parts.length > 1;

    const flat: { blockIndex: number; tokenIndex: number }[] = [];

    content.forEach((_item, blockIndex) => {
      const blockMatches = searchMatches?.[blockIndex];
      if (!blockMatches) return;

      for (let tokenIndex = 0; tokenIndex < blockMatches.length; tokenIndex++) {
        const cur = blockMatches[tokenIndex];
        if (cur?.matched) {
          const prev = tokenIndex > 0 ? blockMatches[tokenIndex - 1] : null;
          const shouldSkipForPhrase = isPhraseSearch && Boolean(prev?.matched);
          if (!shouldSkipForPhrase) {
            flat.push({ blockIndex, tokenIndex });
          }
        }
      }
    });

    return flat;
  }, [content, searchMatches, searchText]);

  const searchMatchIndexMap = useMemo(() => {
    const map: Record<number, Record<number, number>> = {};
    searchMatchPositions.forEach(({ blockIndex, tokenIndex }, globalIndex) => {
      if (!map[blockIndex]) map[blockIndex] = {};
      map[blockIndex][tokenIndex] = globalIndex;
    });
    return map;
  }, [searchMatchPositions]);

  const matchesCount = searchMatchPositions.length;

  useEffect(() => {
    if (isLoading) return;
    if (!player) return;
    const onTimeUpdate = () => {
      const currentTime = player.currentTime();
      setCurrentTimeMs(currentTime * 1000);
    };
    onTimeUpdate();
    player.on('timeupdate', onTimeUpdate);
    return () => {
      player.off('timeupdate', onTimeUpdate);
      setCurrentTimeMs(null);
    };
  }, [player, isLoading]);

  useEffect(() => {
    setActiveSearchMatchIndex(-1);
  }, [searchText, textMode, matchesCount]);

  const scrollToSearchMatch = useCallback(
    (idx: number) => {
      if (!matchesCount) return;
      const safeIdx = ((idx % matchesCount) + matchesCount) % matchesCount;
      shouldScrollToSearchMatchRef.current = true;
      setIsAutoScrolling(false);
      setActiveSearchMatchIndex(safeIdx);
    },
    [matchesCount, setIsAutoScrolling]
  );

  useLayoutEffect(() => {
    if (!shouldScrollToSearchMatchRef.current) return;
    shouldScrollToSearchMatchRef.current = false;
    const el = contentBodyRef.current?.querySelector<HTMLSpanElement>(
      `[data-search-match-index="${activeSearchMatchIndex}"]`
    );
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [activeSearchMatchIndex, matchesCount]);

  useEffect(() => {
    if (!isAutoScrolling) return;
    if (!isCurrentTimeValid) return;
    const el = activeTokenElementRef.current;
    if (!el) return;
    const now = performance.now();
    if (now - lastScrollTsRef.current < 120) return;
    lastScrollTsRef.current = now;
    if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
    rafIdRef.current = requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, [currentTimeMs, isAutoScrolling, isCurrentTimeValid]);

  useEffect(() => {
    return () => {
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
    };
  }, []);

  useEffect(() => {
    const container = contentBodyRef.current;
    if (!container) return;
    const handleUserScrollIntent = () => {
      setIsAutoScrolling(false);
    };
    container.addEventListener('wheel', handleUserScrollIntent);
    container.addEventListener('touchstart', handleUserScrollIntent);
    container.addEventListener('touchmove', handleUserScrollIntent);
    return () => {
      container.removeEventListener('wheel', handleUserScrollIntent);
      container.removeEventListener('touchstart', handleUserScrollIntent);
      container.removeEventListener('touchmove', handleUserScrollIntent);
    };
  }, [setIsAutoScrolling, isLoading]);

  const onActiveTokenRef = useCallback((el: HTMLSpanElement | null) => {
    if (el) activeTokenElementRef.current = el;
  }, []);

  if (isLoading) {
    return (
      <div className="media_viewer_transcription-content_loader">
        <Loader spinSize={98} isSpinning />
      </div>
    );
  }

  if (!content?.length) {
    return (
      <div className="media_viewer_transcription-content_empty">No content</div>
    );
  }

  return (
    <div className="media_viewer_transcription-content">
      <HeadPanel
        setSearchValue={setSearchValue}
        searchText={searchText}
        matchesCount={matchesCount}
        activeSearchMatchIndex={activeSearchMatchIndex}
        scrollToSearchMatch={scrollToSearchMatch}
        textMode={textMode}
        setTextMode={setTextMode}
      />
      <div
        className="media_viewer_transcription-content_body"
        ref={contentBodyRef}
      >
        {content.map((item, index) => (
          <TranscriptionBlock
            key={index}
            item={item}
            currentTimeMs={currentTimeMs}
            isCurrentTimeValid={isCurrentTimeValid}
            searchMatches={searchMatches?.[index]}
            searchMatchIndex={searchMatchIndexMap?.[index]}
            onActiveTokenRef={onActiveTokenRef}
          />
        ))}
      </div>
      <SyncToPlaybackButton
        contentBodyRef={contentBodyRef}
        activeTokenElementRef={activeTokenElementRef}
        isAutoScrolling={isAutoScrolling}
        setIsAutoScrolling={setIsAutoScrolling}
        currentTimeMs={currentTimeMs}
      />
    </div>
  );
}
